import { useEffect, useRef, useState } from 'react';
import { errorContains, userIdFromAddress, } from '@towns-protocol/sdk';
import { check } from '@towns-protocol/dlog';
import { AuthStatus } from './login';
import { TownsClient } from '../client/TownsClient';
import { credentialsToSignerContext, useCredentialStore, } from '../store/use-credential-store';
import { useCasablancaStore } from '../store/use-casablanca-store';
import EventEmitter from 'events';
import { staticAssertNever } from '../utils/towns-utils';
import { useNetworkStatus } from './use-network-status';
import { useSpaceDapp } from './use-space-dapp';
import { useOfflineStore } from '../store/use-offline-store';
import { Err } from '@towns-protocol/proto';
export const useTownsClientListener = (opts) => {
    const { setAuthStatus: setCasablancaAuthStatus, setAuthError: setCasablancaAuthError } = useCasablancaStore();
    const { casablancaCredentialsMap, clearCasablancaCredentials } = useCredentialStore();
    const casablancaCredentials = casablancaCredentialsMap[opts.environmentId ?? ''];
    const [casablancaClient, setCasablancaClient] = useState();
    const [signerContext, setSignerContext] = useState();
    const clientSingleton = useRef();
    const { isOffline } = useNetworkStatus();
    const spaceDapp = useSpaceDapp({ provider: opts.baseProvider, config: opts.baseConfig });
    if (!clientSingleton.current) {
        const townsClient = new TownsClient(opts, spaceDapp);
        clientSingleton.current = new ClientStateMachine(townsClient);
    }
    logServerUrlMismatch(opts.environmentId, clientSingleton);
    useEffect(() => {
        if (!casablancaClient) {
            return;
        }
        const isOnline = !isOffline;
        if (isOnline) {
            casablancaClient.streams.onNetworkStatusChanged(isOnline);
        }
    }, [casablancaClient, isOffline]);
    useEffect(() => {
        const stateMachine = clientSingleton.current;
        if (!stateMachine) {
            return;
        }
        const onStateMachineUpdated = (state) => {
            setCasablancaClient(state.casablancaClient);
            setCasablancaAuthStatus(state.authStatus);
            setSignerContext(state.signerContext);
        };
        const onClearCredentials = (oldCredendials) => {
            clearCasablancaCredentials(opts.environmentId ?? '', oldCredendials);
        };
        const onErrorUpdated = (e) => {
            if (!e) {
                setCasablancaClient(undefined);
                return;
            }
            const _error = e;
            setCasablancaAuthError({
                code: _error?.code ?? 0,
                message: _error?.message ?? `${JSON.stringify(e)}`,
                error: e,
            });
        };
        stateMachine.on('onStateUpdated', onStateMachineUpdated);
        stateMachine.on('onClearCredentials', onClearCredentials);
        stateMachine.on('onErrorUpdated', onErrorUpdated);
        stateMachine.update(casablancaCredentials ?? undefined);
        onStateMachineUpdated(stateMachine.state);
        return () => {
            stateMachine.off('onStateUpdated', onStateMachineUpdated);
            stateMachine.off('onClearCredentials', onClearCredentials);
            stateMachine.off('onErrorUpdated', onErrorUpdated);
        };
    }, [
        casablancaCredentials,
        opts.environmentId,
        setCasablancaClient,
        clearCasablancaCredentials,
        setCasablancaAuthError,
        setCasablancaAuthStatus,
    ]);
    return {
        client: casablancaClient ? clientSingleton.current.client : undefined,
        clientSingleton: clientSingleton.current.client,
        casablancaClient,
        signerContext,
    };
};
class ConnectedToRiver {
    credentials;
    casablancaClient;
    signerContext;
    authStatus = AuthStatus.ConnectedToRiver;
    constructor(credentials, casablancaClient, signerContext) {
        this.credentials = credentials;
        this.casablancaClient = casablancaClient;
        this.signerContext = signerContext;
    }
}
class None {
    authStatus = AuthStatus.None;
    credentials = undefined;
    signerContext = undefined;
    casablancaClient = undefined;
}
class Credentialed {
    credentials;
    signerContext;
    authStatus = AuthStatus.Credentialed;
    casablancaClient = undefined;
    constructor(credentials, signerContext) {
        this.credentials = credentials;
        this.signerContext = signerContext;
    }
}
class EvaluatingCredentials {
    credentials;
    authStatus = AuthStatus.EvaluatingCredentials;
    casablancaClient = undefined;
    signerContext = undefined;
    constructor(credentials) {
        this.credentials = credentials;
    }
}
class DisconnectingFromRiver {
    casablancaClient;
    authStatus = AuthStatus.DisconnectingFromRiver;
    credentials = undefined;
    signerContext = undefined;
    constructor(casablancaClient) {
        this.casablancaClient = casablancaClient;
    }
}
class Deauthenticating {
    authStatus = AuthStatus.Deauthenticating;
    casablancaClient = undefined;
    credentials = undefined;
    signerContext = undefined;
}
function isSituation(state) {
    return state.authStatus === AuthStatus.ConnectedToRiver || state.authStatus === AuthStatus.None;
}
class ClientStateMachine extends EventEmitter {
    state = new None();
    client;
    next = {};
    constructor(client) {
        super();
        this.client = client;
        this.client.on('onCasablancaClientCreated', (client) => {
            this.updateClient(client);
        });
    }
    update(nextCredentials) {
        this.next = { credentials: nextCredentials };
        void this.tick();
    }
    updateClient(client) {
        if (this.state instanceof Credentialed) {
            console.log('[use-towns-client-listener] connected to river after client update');
            this.state = new ConnectedToRiver(this.state.credentials, client, this.state.signerContext);
            this.emit('onStateUpdated', this.state);
        }
    }
    async tick() {
        if (!isSituation(this.state)) {
            // console.log('$$$ tick: ignoring re-entry')
            return;
        }
        const transition = getTransition(this.state, this.next);
        if (!transition) {
            // console.log('$$$ tick: no transition needed')
            return;
        }
        logTick(this.client, this.state, transition);
        const currentSituation = this.state;
        this.state = transition;
        this.emit('onStateUpdated', this.state);
        this.state = await this.execute(currentSituation, transition);
        logTick(this.client, this.state);
        this.emit('onStateUpdated', this.state);
        void this.tick(); // call ourself again to pick up any pending next state
    }
    async execute(currentSituation, transition) {
        switch (transition.authStatus) {
            case AuthStatus.DisconnectingFromRiver:
                check(currentSituation instanceof ConnectedToRiver);
                await this.client.stopCasablancaClient();
                return new None();
            case AuthStatus.Deauthenticating:
                check(currentSituation instanceof Credentialed);
                return new None();
            case AuthStatus.EvaluatingCredentials: {
                check(currentSituation instanceof None);
                this.emit('onErrorUpdated', undefined);
                const { credentials } = transition;
                const newState = await logInWithRetries(credentials, this.client, this);
                return newState;
            }
            default:
                staticAssertNever(transition);
        }
    }
}
async function logInWithRetries(credentials, client, emitter) {
    let retryCount = 0;
    const MAX_RETRY_COUNT = 20;
    // eslint-disable-next-line no-constant-condition
    while (true) {
        const context = credentialsToSignerContext(credentials);
        try {
            console.log('[use-towns-client-listener] logging in');
            const userId = userIdFromAddress(context.creatorAddress);
            const isRegistered = useOfflineStore.getState().skipIsRegisteredCheck[userId] ||
                (await fetchIsRegistered(client, context));
            if (!isRegistered) {
                console.log("[use-towns-client-listener] user authenticated, hasn't joined a space");
                return new Credentialed(credentials, context);
            }
            else {
                console.log('[use-towns-client-listener] user authenticated, starting casablanca client');
                useOfflineStore.getState().setSkipIsRegisteredCheck(userId);
                const casablancaClient = await client.startCasablancaClient(context);
                return new ConnectedToRiver(credentials, casablancaClient, context);
            }
        }
        catch (e) {
            retryCount++;
            console.error('[use-towns-client-listener] casablanca client encountered exception', e);
            try {
                await client.logoutFromCasablanca();
            }
            catch (e) {
                console.error('error while logging out', e);
            }
            if (errorContains(e, Err.BAD_STREAM_CREATION_PARAMS)) {
                // if the user streams failed to create, just return in the credentialed state, it should resolve if the user joins or creates a space
                return new Credentialed(credentials, context);
            }
            // aellis - if you have credentials you should always be able to sign in to the stream node
            // this error is probably a connect error, so we should just retry forever probably?
            // but in the future we will have expiring delegate signatures, and perhaps rate limiting? which
            // would necessitate a better conditions here
            if (retryCount >= MAX_RETRY_COUNT) {
                emitter.emit('onClearCredentials', credentials);
                emitter.emit('onErrorUpdated', e);
                return new None();
            }
            else {
                const retryDelay = getRetryDelay(retryCount);
                console.log('[use-towns-client-listener] retrying', { retryDelay, retryCount });
                // sleep
                await new Promise((resolve) => setTimeout(resolve, retryDelay));
            }
        }
    }
}
// exponentially back off, but never wait more than 20 seconds
function getRetryDelay(retryCount) {
    return Math.min(1000 * 2 ** retryCount, 20000);
}
function getTransition(current, next) {
    switch (current.authStatus) {
        case AuthStatus.None:
            if (next.credentials !== undefined) {
                return new EvaluatingCredentials(next.credentials);
            }
            break;
        case AuthStatus.ConnectedToRiver:
            if (next.credentials !== current.credentials) {
                return new DisconnectingFromRiver(current.casablancaClient);
            }
            break;
        case AuthStatus.Credentialed:
            if (next.credentials != current.credentials) {
                return new Deauthenticating();
            }
            break;
        default:
            staticAssertNever(current);
    }
    return undefined;
}
function logTick(client, situation, transition) {
    console.log('$$$ tick update', {
        client: client.name,
        current: situation.authStatus,
        transition: transition?.authStatus,
    });
}
function logServerUrlMismatch(environmentId, clientSingleton) {
    if (environmentId !== clientSingleton.current?.client.opts.environmentId) {
        // aellis there is an assumption in this code that the environmentId url never changes
        // if it does change, we need to refresh the page to recreate the client
        console.error("$$$ use towns client listener: environmentId doesn't match", {
            environmentId: environmentId,
            client: clientSingleton.current?.client.opts.environmentId,
        });
    }
}
async function fetchIsRegistered(client, context) {
    console.log('[use-towns-client-listener] fetching Is registered State for', context.creatorAddress);
    const unauthedClient = await client.makeUnauthenticatedClient();
    return await unauthedClient.userWithAddressExists(context.creatorAddress);
}
//# sourceMappingURL=use-towns-client-listener.js.map